import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

interface CookieConsent {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

const CookieBanner: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [consent, setConsent] = useState<CookieConsent>({
    necessary: true, // Always required
    analytics: false,
    marketing: false,
    preferences: false
  });

  useEffect(() => {
    // Check if consent has been given before
    const storedConsent = localStorage.getItem('cookieConsent');
    if (!storedConsent) {
      setIsOpen(true);
    } else {
      setConsent(JSON.parse(storedConsent));
    }
  }, []);

  const handleAcceptAll = () => {
    const newConsent: CookieConsent = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    };
    saveConsent(newConsent);
  };

  const handleSavePreferences = () => {
    saveConsent(consent);
  };

  const saveConsent = (consentData: CookieConsent) => {
    // Save consent to localStorage
    localStorage.setItem('cookieConsent', JSON.stringify(consentData));
    
    // Set consent cookie with 1 year expiry
    const expiryDate = new Date();
    expiryDate.setFullYear(expiryDate.getFullYear() + 1);
    document.cookie = `cookieConsent=${JSON.stringify(consentData)}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
    
    setConsent(consentData);
    setIsOpen(false);
    setShowPreferences(false);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        className="fixed bottom-0 left-0 right-0 z-50 p-4 w-full max-w-[100vw]"
      >
        <div className="max-w-7xl mx-auto w-full">
          <div className="bg-white dark:bg-dark-800 rounded-xl shadow-lg border border-gray-200 dark:border-dark-700 w-full">
            {!showPreferences ? (
              <div className="p-4 sm:p-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <Cookie className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div className="ml-3 sm:ml-4 flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      We value your privacy
                    </h3>
                    <p className="mt-1 sm:mt-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                      We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. 
                      By clicking "Accept All", you consent to our use of cookies.
                    </p>
                    <div className="mt-3 sm:mt-4 flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                      <button
                        onClick={handleAcceptAll}
                        className="w-full sm:w-auto px-3 sm:px-4 py-1.5 sm:py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-xs sm:text-sm font-medium"
                      >
                        Accept All
                      </button>
                      <button
                        onClick={() => setShowPreferences(true)}
                        className="w-full sm:w-auto px-3 sm:px-4 py-1.5 sm:py-2 border border-gray-300 dark:border-dark-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors text-xs sm:text-sm font-medium"
                      >
                        Customize Preferences
                      </button>
                      <div className="text-xs sm:text-sm">
                        <Link 
                          to="/privacy" 
                          className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300"
                        >
                          Privacy Policy
                        </Link>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="flex-shrink-0 ml-4 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Cookie Preferences
                  </h3>
                  <button
                    onClick={() => setShowPreferences(false)}
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-dark-700 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">Necessary</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Required for the website to function properly</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={consent.necessary}
                      disabled
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded cursor-not-allowed"
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-dark-700 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">Analytics</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Help us improve by tracking anonymous usage data</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={consent.analytics}
                      onChange={(e) => setConsent(prev => ({ ...prev, analytics: e.target.checked }))}
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded cursor-pointer"
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-dark-700 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">Marketing</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Allow us to personalize your experience</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={consent.marketing}
                      onChange={(e) => setConsent(prev => ({ ...prev, marketing: e.target.checked }))}
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded cursor-pointer"
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-dark-700 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">Preferences</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Store your website preferences</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={consent.preferences}
                      onChange={(e) => setConsent(prev => ({ ...prev, preferences: e.target.checked }))}
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded cursor-pointer"
                    />
                  </div>
                </div>
                <div className="mt-6 flex justify-end space-x-4">
                  <button
                    onClick={() => setShowPreferences(false)}
                    className="px-4 py-2 border border-gray-300 dark:border-dark-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSavePreferences}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
                  >
                    Save Preferences
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieBanner;